import { useEffect, useState } from 'react';
import { Controller, useForm, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Collapse,
  Alert,
  AlertTitle,
  Box,
  Tab,
  TextField,
  Checkbox,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
} from '@mui/material';
import {
  EActionType,
  EAction,
  RegExPatterns,
  TableHeaders,
  foreignKeysForTable,
  ECellType,
} from '../../../data/Constants';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import { AppDispatch, AppState, AppStore } from '../../../store';
import { resetAlertMessage, setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from '../../ErrorMessage';
import { resetError } from '../../../store/states/ErrorSlice';
import {
  createUser,
  getUserDetails,
  getUsers,
  updateUser,
  updateUserRoleAssociation,
} from '../../../store/states/UserSlice';
import { TabContext, TabList } from '@mui/lab';
import { resetAllErrorInfo } from '../../../services/DataHelperFunctions';
import { getAllRoles } from '../../../store/states/RoleSlice';
import { TFunction } from 'i18next';

import type {
  IAPIResponse,
  IError,
  IManipulationProps,
  IRole,
  ITableHeaders,
  IUser,
} from '../../../../types';

const uniqueKeyForRole: string = foreignKeysForTable['roles'];
const stringValidations = {
  name: { min: 3 },
  word: { max: 2 },
};

const handleCreate = (formData: IUser, selected: string[], dispatch: AppDispatch, t: TFunction) => {
  dispatch(
    createUser({
      request: { userInfo: { ...formData, name: formData.name?.trimEnd() }, roles: selected },
    })
  ).then((res) => {
    handleAPIResponse(
      res.payload as IAPIResponse,
      dispatch,
      t('messages.fail.create'),
      t('messages.success.create')
    );
  });
};
const handleAPIResponse = (
  res: IAPIResponse,
  dispatch: AppDispatch,
  errorMessage: string,
  successMessage: string
) => {
  const users = AppStore.getState().administration.users;

  if (res?.data) {
    dispatch(
      getUsers({
        page: users.currentPage,
        limit: users.recordsPerPage,
        searchCode: users.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error?.message ? res.error.message : errorMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
};
const handleUpdateUserDetails = (formData: IUser, dispatch: AppDispatch, t: TFunction) => {
  dispatch(updateUser({ request: { ...formData, name: formData.name?.trimEnd() } })).then((res) => {
    handleAPIResponse(
      res.payload as IAPIResponse,
      dispatch,
      t('messages.fail.update'),
      t('messages.success.update')
    );
  });
};
const handleUpdateUserRoles = (formData: IUser, dispatch: AppDispatch, t: TFunction) => {
  dispatch(
    updateUserRoleAssociation({
      request: { emailId: formData.emailId, roles: formData.roles },
    })
  ).then((res) => {
    handleAPIResponse(
      res.payload as IAPIResponse,
      dispatch,
      t('messages.fail.update'),
      t('messages.success.update')
    );
  });
};
const handleUpdateCompleteUser = (formData: IUser, dispatch: AppDispatch, t: TFunction) => {
  dispatch(updateUser({ request: { ...formData, name: formData.name?.trimEnd() } })).then((res) => {
    if (
      !res ||
      (res.payload &&
        typeof res.payload === 'object' &&
        'error' in res.payload &&
        (res.payload as IAPIResponse).error)
    ) {
      const message =
        res?.payload &&
        typeof res.payload === 'object' &&
        'error' in res.payload &&
        (res.payload as IAPIResponse).error?.message
          ? (res.payload as IAPIResponse).error.message
          : t('messages.fail.update');
      dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
    } else {
      handleUpdateUserRoles(formData, dispatch, t);
    }
  });
};
const handleError = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
  if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
    setDisplayAlertMessage(true);
  }
};
const loadFormData = async (
  props: IManipulationProps,
  setFormData: (data: IUser) => void,
  dispatch: AppDispatch,
  setInitialData: (data: IUser) => void,
  reset: (values?: IUser) => void,
  setValue: UseFormSetValue<IUser>
) => {
  let userDetails = await dispatch(getUserDetails({ request: [props.id] })).unwrap();
  if (!('data' in userDetails) || !userDetails.data) {
    return;
  }

  const userData = userDetails.data[0];
  const assignedRoles = userData.roles.map((role: IRole) => {
    return role[uniqueKeyForRole];
  });
  userData.roles = assignedRoles;
  setFormData(userData);
  setInitialData(userData);
  reset(userData);

  setValue('roles', assignedRoles);
};

const createCell = (row: { [key: string]: any }, c: ITableHeaders): React.ReactNode => {
  if (c.type === ECellType.Checkbox) {
    return <Checkbox value={row[c.field]} checked={row[c.field]} disabled />;
  } else {
    return row[c.field];
  }
};

const addDefaultRole = (
  defaultRole: string,
  manipulationType: string,
  setValue: UseFormSetValue<IUser>
) => {
  if (defaultRole && manipulationType === EActionType.Create) {
    setValue('roles', [defaultRole]);
  }
};
const disableRole = (
  row: IRole,
  manipulationType: string,
  formData: IUser,
  getValues: UseFormGetValues<IUser>
) => {
  if (manipulationType === EActionType.Edit) {
    if (!getValues('isActive')) {
      return true;
    } else if (formData.roles && row.isDefault) {
      return formData.roles.includes(row.name);
    }
  } else if (manipulationType === EActionType.Create && row.isDefault) {
    return true;
  }
  return false;
};

const getTestId = (fieldName: string): string | undefined => {
  if (fieldName === 'isAce') {
    return 'user-isAce-input';
  }
  if (fieldName === 'isActive') {
    return 'user-isActive-input';
  }
  return undefined;
};
export const UserManipulationForm = (props: IManipulationProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector((state: AppState) => state.error);
  const roles =
    (AppStore.getState().roles.data as IRole[] | undefined)?.filter((role) => role.isActive) || [];

  const [displayAlertMessage, setDisplayAlertMessage] = useState(false);
  const [defaultRole, setDefaultRole] = useState<string>();
  const [tabValue, setTabValue] = useState('PersonalDetails');
  const initdata = {
    name: '',
    emailId: '',
    isAdmin: false,
    isActive: true,
    isService: false,
    isPdm: false,
    isAce: false,
    roles: [] as string[],
  };
  const [initialData, setInitialData] = useState<IUser>(initdata);
  const [formData, setFormData] = useState<IUser>(initdata);
  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: formData,
  });
  useEffect(() => {
    dispatch(getAllRoles({ page: 0, limit: 0, searchCode: '' }));
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setFormData, dispatch, setInitialData, reset, setValue);
    }
  }, []);
  useEffect(() => {
    handleError(error, setDisplayAlertMessage);
  }, [error]);
  useEffect(() => {
    const tempDefaultRole: string = roles?.find((r) => r.isDefault)?.name ?? '';
    setDefaultRole(tempDefaultRole);
  }, [roles]);

  useEffect(() => {
    if (defaultRole) {
      addDefaultRole(defaultRole, manipulationType, setValue);
    }
  }, [defaultRole]);
  const onSubmit = (data: IUser, watchedValues: Record<string, any>, initialData: IUser) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    const fieldsToCheck = ['name', 'emailId', 'isAdmin', 'isService', 'isPdm', 'isAce', 'isActive'];

    const personalDetailsUpdated = fieldsToCheck.filter(
      (field) => watchedValues[field as keyof IUser] !== initialData[field as keyof IUser]
    );
    const rolesUpdated =
      JSON.stringify(data.roles?.sort()) !== JSON.stringify(initialData.roles?.sort());
    switch (manipulationType) {
      case EActionType.Create:
        handleCreate(data, data.roles ?? [], dispatch, t);
        break;
      case EActionType.Edit:
        if (personalDetailsUpdated && rolesUpdated) {
          handleUpdateCompleteUser(data, dispatch, t);
        } else if (personalDetailsUpdated) {
          handleUpdateUserDetails(data, dispatch, t);
        } else if (rolesUpdated) {
          handleUpdateUserRoles(data, dispatch, t);
        }
        break;
    }
  };

  const handleSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>,
    initialUserData: IUser,
    defaultRole: string
  ) => {
    if (event.target.checked) {
      setValue(
        'roles',
        roles.map((role) => role.name),
        { shouldDirty: true, shouldValidate: true }
      );
    } else if (
      initialUserData.roles?.includes(defaultRole) ||
      manipulationType === EActionType.Create
    ) {
      setValue('roles', [defaultRole], { shouldDirty: true, shouldValidate: true });
    } else {
      setValue('roles', [], { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(resetAlertMessage());
    dispatch(resetError());
    setDisplayAlertMessage(false);
  };
  const manipulationType = props.type;
  const handleTabChange = (...params: [React.BaseSyntheticEvent, string]) => {
    setTabValue(params[1]);
  };

  const accessTypes = [
    { code: 'isAdmin', name: 'isAdmin' },
    { code: 'isService', name: 'isService' },
    { code: 'isPdm', name: 'isPdm' },
    { code: 'isAce', name: 'isAce' },
  ];
  if (manipulationType === EActionType.Edit && !initialData.isActive) {
    accessTypes.push({ code: 'isActive', name: 'isActive' });
  }
  const watchedValues = watch(); // watches all fields
  const submitForm = (data: IUser) => {
    onSubmit(data, watchedValues, initialData);
  };
  const headers = TableHeaders['roleAssociationTab'].main;
  const allSelected = roles.every((row) => watchedValues.roles?.includes(row.name));
  const someSelected = roles.some((row) => watchedValues.roles?.includes(row.name));

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: { value: string[] | undefined; onChange: (value: string[]) => void },
    name: string
  ): void => {
    const value = field.value ?? [];
    if (e.target.checked) {
      field.onChange([...value, name]); // Add the object
    } else {
      field.onChange(value.filter((selected) => selected !== name)); // Remove the object
    }
  };
  const checkFieldDisabled = (name: string) => {
    if (name === 'isActive' && manipulationType === EActionType.Edit && !getValues('isActive')) {
      return false;
    }
    return manipulationType === EActionType.Edit && !getValues('isActive');
  };
  return (
    <Box className="userManipulationTabs">
      <form onSubmit={handleSubmit(submitForm)}>
        <TabContext value={tabValue || 'PersonalDetails'}>
          <Box className="dialog-box">
            <TabList onChange={handleTabChange}>
              <Tab
                data-testid="user-personalDetails-tab"
                className="dialog-tab text-capitalize"
                value="PersonalDetails"
                label={
                  <span>
                    {t('labels.personalDetails')} <span className="asterisk">*</span>
                  </span>
                }
              />
              <Tab
                data-testid="user-roleAssociation-tab"
                className="dialog-tab text-capitalize"
                value="RoleAssociation"
                label={t('labels.roleAssociation')}
              />
            </TabList>
          </Box>
          <div className={tabValue === 'PersonalDetails' ? 'personalDetailsTab show' : 'hide'}>
            <Box className="personalDetailsForm">
              <Collapse in={displayAlertMessage} className="show-alert">
                <Alert className="errorMessage" severity="error" onClose={handleClose}>
                  <AlertTitle>
                    <ErrorMessage error={error} manipulationType={manipulationType} />
                  </AlertTitle>
                </Alert>
              </Collapse>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: { value: true, message: 'nameErrorMessage' },
                  minLength: { value: stringValidations.name.min, message: 'minLimitErrorMessage' },
                  pattern: { value: RegExPatterns.UserName, message: 'nameErrorMessage' },
                }}
                render={({ field }) => (
                  <TextField
                    className="mt-1em"
                    {...field}
                    label={
                      <>
                        {t('labels.name')}
                        <span className="asterisk">*</span>
                      </>
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={manipulationType === EActionType.Edit && !getValues('isActive')}
                    slotProps={{
                      htmlInput: { 'data-testid': 'user-name-input' },
                    }}
                  />
                )}
              />

              <br />
              <Collapse in={!!errors['name']}>
                <Alert className="errorMessage" severity="error">
                  <AlertTitle>{t('messages.nameErrorMessage')} </AlertTitle>
                </Alert>
              </Collapse>
              <Controller
                name="emailId"
                control={control}
                rules={{
                  required: { value: true, message: 'emailErrorMessage' },
                  pattern: { value: RegExPatterns.Email, message: 'emailErrorMessage' },
                }}
                render={({ field }) => (
                  <TextField
                    className="mt-1em"
                    {...field}
                    label={
                      <>
                        {t('labels.email')}
                        <span className="asterisk">*</span>
                      </>
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={manipulationType === EActionType.Edit}
                    slotProps={{
                      htmlInput: { 'data-testid': 'user-email-input' },
                    }}
                  />
                )}
              />

              <br />
              <Collapse in={!!errors['emailId']}>
                <Alert className="errorMessage" severity="error">
                  <AlertTitle>{t('messages.emailErrorMessage')} </AlertTitle>
                </Alert>
              </Collapse>

              <fieldset className="inputFieldset w-100 mt-1em">
                <legend className="inputLegend" id="views">
                  {t('labels.userAccessType')}
                </legend>
                <div className="checkbox-grid">
                  {accessTypes.map((fieldConfig) => (
                    <Controller
                      key={fieldConfig.name}
                      name={fieldConfig.name as keyof IUser}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          label={t(`${'labels.' + fieldConfig.name}`)}
                          labelPlacement="end"
                          className="mt-1em"
                          control={
                            <Checkbox
                              {...field}
                              checked={!!field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                              }}
                              disabled={checkFieldDisabled(fieldConfig.name)}
                              data-testid={getTestId(fieldConfig.name)}
                            />
                          }
                        />
                      )}
                    />
                  ))}
                </div>
              </fieldset>
            </Box>
          </div>
          <div className={tabValue === 'RoleAssociation' ? 'roleAssociationTab show' : 'hide'}>
            <TableContainer component={Paper} className="data-table">
              <Table stickyHeader aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={someSelected && !allSelected}
                        checked={allSelected}
                        onChange={(e) => {
                          handleSelectAll(e, initialData, defaultRole ?? '');
                        }}
                        disabled={manipulationType === EActionType.Edit && !getValues('isActive')}
                        data-testid="user-role-select-all"
                      />
                    </TableCell>
                    {headers.map((row) => (
                      <TableCell key={row.field}>{t('labels.' + row.field)}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {roles.map((row) => {
                    const isItemSelected = watchedValues.roles?.includes(row.name);
                    return (
                      <TableRow
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        hover
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.name}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Controller
                            name="roles"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={(field.value ?? []).includes(row.name)}
                                onChange={(e) => handleCheckboxChange(e, field, row.name)}
                                disabled={disableRole(row, manipulationType, formData, getValues)}
                              />
                            )}
                          />
                        </TableCell>
                        {headers.map((c) => (
                          <TableCell key={c.field} component="th" scope="row">
                            {createCell(row, c)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </TabContext>
        <Box className="formActionButtons">
          <Button
            type="submit"
            data-testid="role-button-submit"
            variant="contained"
            size="medium"
            className="text-capitalize"
            disabled={!isValid || !isDirty}
          >
            {manipulationType === EActionType.Create ? t('button.create') : t('button.save')}
          </Button>
          <Button
            data-testid="user-button-cancel"
            size="medium"
            className="text-capitalize"
            onClick={() => {
              resetAllErrorInfo();
              setDisplayAlertMessage(false);
            }}
          >
            {t('button.cancel')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
